import { useEffect, useState } from "react";
import { Drawer, Layout as LayoutAnt, Menu } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCartOutlined,
  ControlOutlined,
  HomeOutlined,
  CalculatorOutlined,
  LockOutlined,
  FileTextOutlined,
  BarChartOutlined,
} from "@ant-design/icons";

import { isMobile } from "react-device-detect";

const { Sider } = LayoutAnt;

const routes = [
  {
    key: "dashboard",
    url: "/dashboard",
    icon: <HomeOutlined />,
    label: "Dashboard",
    permissions: ["dashboard-dashboard:read", "dashboard-dashboard:write"],
  },
  {
    key: "sales",
    url: "/sales",
    icon: <ShoppingCartOutlined />,
    label: "Vendas",
    permissions: ["sales-sales:read", "sales-sales:write"],
  },
  {
    key: "contracts",
    url: "/contracts",
    icon: <FileTextOutlined />,
    label: "Contratos",
    permissions: ["contracts-contracts:read", "contracts-contracts:write"],
  },
  {
    key: "reports",
    url: "/reports",
    icon: <BarChartOutlined />,
    label: "Relatórios",
    permissions: ["reports-reports:read", "reports-reports:write"],
  },
  {
    key: "expenses",
    url: "/expenses",
    icon: <CalculatorOutlined />,
    label: "Despesas",
    disabled: false,
    children: [
      {
        key: "expenses/expenses",
        label: "Despesas",
        url: "/expenses/expenses",
        permissions: [
          "expenses-expenses:read",
          "expenses-expenses:write",
        ]
      },
      {
        key: "expenses/categories",
        label: "Categorias",
        url: "/expenses/categories",
        permissions: [
          "expenses-categories:read",
          "expenses-categories:write",
        ],
      }
    ]
  },
  {
    key: "management",
    url: "/management",
    icon: <ControlOutlined />,
    label: "Gestão",
    disabled: false,
    children: [
      {
        key: "management/customers",
        label: "Clientes",
        url: "/management/customers",
        permissions: ["customers-customers:read", "customers-customers:write"],
      },
      {
        key: "management/plans",
        label: "Planos",
        url: "/management/plans",
        permissions: ["plans-plans:read", "plans-plans:write"],
      },
      {
        key: "management/services",
        label: "Serviços",
        url: "/management/services",
        permissions: ["services-services:read", "services-services:write"],
      }
    ],
  },
  {
    key: "admin",
    url: "/admin",
    icon: <LockOutlined />,
    label: "Administração",
    disabled: false,
    children: [
      {
        key: "admin/users",
        label: "Usuários",
        url: "/admin/users",
        permissions: ["users-users:read", "users-users:write"],
      },
      {
        key: "admin/profiles",
        label: "Perfis",
        url: "/admin/profiles",
        permissions: ["users-profiles:read", "users-profiles:write"],
      },
      {
        key: "admin/account",
        label: "Conta",
        url: "/admin/account",
        permissions: ["accounts-accounts:read", "accounts-accounts:write"],
      }
    ]
  }
];

export default function SiderCustom({
  modulePermissions,
  toggleCollapsed,
  collapsed,
  setTitle,
}: any) {
  const navigate = useNavigate();

  const [currentKey, setCurrentKey]: any = useState(null);
  const [currentComponent, setCurrentComponent] = useState(null);
  const [oldCurrentKey, setOldCurrentKey] = useState(null);
  const [isOnlyModule, setIsOnlyModule] = useState(false);
  const [currentReminder, setCurrentReminder] = useState(null);
  const [authorizedRoutes, setAuthorizedRoutes] = useState<any[]>([]);

  const findRouteByPathname = (pathname: String, items: any) => {
    let routeFound = items.find((route: any) => pathname == route.url);

    if (!routeFound) {
      for (let route of items) {
        if (route.children && route.children.length) {
          routeFound = findRouteByPathname(pathname, route.children);

          if (routeFound) {
            routeFound.childLabel = [route.label, routeFound.label].join(" / ");
            return routeFound;
          }
        }
      }
    }

    return routeFound;
  };

  const filterAuthorizedRoutes = (routes: any[]) => {
    return routes.filter((route: any) => {
      if (route?.children?.length) {
        route.children = filterAuthorizedRoutes(route.children);

        if (route.children.length) {
          return route;
        }
      }

      if (
        route?.permissions?.some((permission: string) =>
          modulePermissions.includes(permission)
        )
      ) {
        return route;
      }
    });
  };

  useEffect(() => {
    if (window.location.pathname) {
      let pathname = window.location.pathname;

      if (pathname === "/") {
        pathname = "/dashboard";
      }

      const splittedPathname = pathname.substring(1).split("/");

      if (splittedPathname.length > 2) {
        pathname = "/" + splittedPathname.slice(0, 2).join("/");
      }

      const routeFound = findRouteByPathname(pathname, authorizedRoutes);

      if (routeFound) {
        setTitle(routeFound.childLabel || routeFound.label);
        delete routeFound.childLabel;
        setCurrentKey(routeFound.key);
      }
    }
  }, [window.location.pathname, authorizedRoutes]);

  useEffect(() => {
    if (modulePermissions.length) {
      if (Object.keys(modulePermissions).length === 1) {
        let [module, permission] = Object.keys(modulePermissions)[0].split("-");

        const onlyRoute: any = routes.find((route) => route.key === module);

        if (onlyRoute) {
          setIsOnlyModule(true);
          setCurrentKey(onlyRoute.key);
          goToModule(onlyRoute.key);
        }
      } else {
        setAuthorizedRoutes(filterAuthorizedRoutes(routes));
      }
    }
  }, [modulePermissions]);

  const goToModule = (key: string) => {
    if (!window.location.href.includes(key)) {
      navigate(key);
    }
  };

  function MenuComponent({ routes, ...props }: any) {
    return (
      <Menu
        mode="inline"
        onClick={({ key }) => {
          let componentRoute = routes.find((route: any) => route.key === key);

          if (!componentRoute) {
            routes.forEach((route: any) => {
              if (route.children) {
                const childRoute = route.children.find(
                  (child: any) => child.key === key
                );

                if (childRoute) {
                  childRoute.childLabel = [route.label, childRoute.label].join(
                    " / "
                  );
                  componentRoute = childRoute;
                }
              }
            });
          }

          if (!componentRoute) return;

          setTitle(componentRoute.childLabel || componentRoute.label);
          delete componentRoute.childLabel;

          if (componentRoute?.component) {
            setCurrentComponent(componentRoute.component);
            setOldCurrentKey(currentKey);
            setCurrentKey(key);
            return;
          }

          setCurrentKey(key);
          navigate(key);
        }}
        defaultSelectedKeys={[currentKey]}
        defaultOpenKeys={[currentKey ? currentKey.split("/")[0] : currentKey]}
        items={routes.map((route: any) => {
          return {
            key: route.key,
            icon: route.icon,
            label: route.label,
            children: route.children,
            disabled: route.disabled,
          };
        })}
      />
    );
  }

  return (
    <>
      {!isOnlyModule &&
        (!isMobile ? (
          <Sider
            breakpoint="lg"
            collapsedWidth="80"
            collapsible
            collapsed={collapsed}
            trigger={null}
            style={{
              background: "#FFFFFF",
              boxShadow: "2px 0px 6px #0000001A",
              zIndex: 99,
            }}
          >
            {!collapsed ? (
              <a href="/dashboard" className="nav-link">
                <img src="/logo.png" className="client_logo" alt="logo" />
              </a>
            ) : (
              <a href="/dashboard" className="nav-link">
                <img src="/logo-minimizada.png" className="client_logo_minimizada" alt="logo minimizada" />
              </a>
            )}

            <MenuComponent routes={authorizedRoutes} />
          </Sider>
        ) : (
          <Drawer
            open={collapsed}
            placement="left"
            closeIcon={false}
            width={270}
            onClose={toggleCollapsed}
            style={{ background: "#FFFFFF", zIndex: 99 }}
            title={
              <a href="/dashboard" className="nav-link">
                <img src="/logo.png" className="client_logo" alt="logo" />
              </a>
            }
          >
            <MenuComponent routes={authorizedRoutes} />
          </Drawer>
        ))}
    </>
  );
}
